import { CommunityEntityType, CreateCommentPayload } from '../../types/community'
import { RightArrowSvgIcon } from '../util/images'
import { X } from 'lucide-react'
import React, { useEffect, useImperativeHandle, useRef, useState } from 'react'
import { useCreateComment } from 'src/hook/use-community'
import { toasterUtil } from 'src/util/toaster-util'

export interface ReplyContext {
  commentId: string
  username: string
}

export interface CommentFormHandle {
  focus: () => void
}

interface CommentFormProps {
  entityType: CommunityEntityType
  entityId: string
  replyTo?: ReplyContext | null
  onClearReply?: () => void
  onSubmit?: (payload: CreateCommentPayload) => Promise<void>
}

export const CommentForm = React.forwardRef<CommentFormHandle, CommentFormProps>(
  ({ entityId, entityType, replyTo, onClearReply, onSubmit }, ref) => {
    const [text, setText] = useState<string>('')
    const [error, setError] = useState<string | undefined>(undefined)
    const [isFocused, setIsFocused] = useState<boolean>(false)
    const textareaRef = useRef<HTMLTextAreaElement>(null)
    const maxLength = 500
    const createComment = useCreateComment()

    useImperativeHandle(ref, () => ({
      focus: () => textareaRef.current?.focus(),
    }))

    const calculateRows = (): number => {
      if (!text) return 1
      const lineCount = text.split('\n').length
      const charBasedRows = Math.ceil(text.length / 50)
      return Math.min(Math.max(lineCount, charBasedRows, 1), 6)
    }

    useEffect(() => {
      if (text.length <= maxLength) {
        setError(undefined)
      }
    }, [text])

    const handleSubmit = async (event: React.FormEvent): Promise<void> => {
      event.preventDefault()
      if (!text.trim()) {
        setError('Comment cannot be empty')
        return
      }
      if (text.length > maxLength) {
        setError('Comment is too long')
        return
      }

      const payload: CreateCommentPayload = {
        text: text.trim(),
        entityId,
        entityType,
        parentCommentId: replyTo?.commentId ?? null,
      }

      try {
        await createComment.mutateAsync({ payload })
        setText('')
        if (onClearReply) onClearReply()
        if (onSubmit) await onSubmit(payload)
      } catch (err) {
        toasterUtil.error(err instanceof Error ? err.message : 'Failed to post comment')
      }
    }

    const remaining = `${text.length}/${maxLength}`
    const isValid = text.trim() && text.length <= maxLength && !createComment.isLoading

    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-1">
        {replyTo && (
          <div className="flex items-center gap-2 px-2 py-1">
            <span className="text-xs text-white/50">
              Replying to <span className="font-semibold text-primary">{replyTo.username}</span>
            </span>
            <button type="button" onClick={onClearReply} className="text-white/30 hover:text-white/60">
              <X className="w-3 h-3" />
            </button>
          </div>
        )}
        <div className="flex items-start gap-3">
          <textarea
            ref={textareaRef}
            className="flex-1 resize-none bg-transparent border-b border-white/20 px-2 py-2 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-primary transition-colors"
            placeholder={replyTo ? `Reply to ${replyTo.username}...` : 'Add a comment...'}
            value={text}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault()
                event.currentTarget.form?.requestSubmit()
              }
              if (event.key === 'Escape' && replyTo && onClearReply) {
                onClearReply()
              }
            }}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            maxLength={maxLength + 1}
            disabled={createComment.isLoading}
            rows={calculateRows()}
          />
          <button
            type="submit"
            disabled={!isValid}
            className={`flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-full transition-all ${
              isValid ? 'bg-primary hover:bg-primary/80' : 'bg-white/10 cursor-not-allowed'
            }`}
            title="Post comment"
          >
            <RightArrowSvgIcon
              className={`w-4 h-4 [&_path]:transition-all ${isValid ? '[&_path]:fill-white' : '[&_path]:fill-white/40'}`}
            />
          </button>
        </div>
        {(isFocused || error) && (
          <div className="flex items-center justify-between text-sm text-white/60 px-2">
            <span>{remaining}</span>
            {error && <span className="text-red-400">{error}</span>}
          </div>
        )}
      </form>
    )
  },
)

CommentForm.displayName = 'CommentForm'
